import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "travel_erp_token";

const AuthContext = createContext(null);

// Default menu visibility — must stay in sync with backend DEFAULT_MENU_VISIBILITY
const DEFAULT_MENU_VISIBILITY = {
  dashboard:   ["admin", "manager", "sales_agent", "accounts"],
  quick_quote: ["admin", "manager", "sales_agent"],
  packages:    ["admin", "manager", "sales_agent", "accounts"],
  pipeline:    ["admin", "manager", "sales_agent"],
  templates:   ["admin", "manager", "sales_agent"],
  customers:   ["admin", "manager", "sales_agent"],
  vendors:     ["admin", "manager"],
  hotels:      ["admin", "manager", "sales_agent"],
  reports:     ["admin", "manager", "accounts"],
  referrers:   ["admin", "manager"],
  my_referrals: ["referrer"],
  master_data: ["admin", "manager"],
  users:       ["admin"],
  audit_log:   ["admin", "manager"],
  settings:    ["admin"],
};

// Send cookies with cross-origin requests (needed for httpOnly cookie auth)
axios.defaults.withCredentials = true;

// Global axios interceptor - attach Bearer as fallback (cookie is primary)
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token && config.url?.startsWith(BACKEND_URL)) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error.response?.status === 401) {
      // Session expired - clear local token and redirect to login
      const wasAuthed = !!localStorage.getItem(TOKEN_KEY);
      localStorage.removeItem(TOKEN_KEY);
      if (wasAuthed && !window.location.pathname.startsWith("/login")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(undefined); // undefined = loading, null = unauth
  const [menuVisibility, setMenuVisibility] = useState(DEFAULT_MENU_VISIBILITY);

  useEffect(() => {
    // Always try /auth/me — cookie may be present even without localStorage token
    axios.get(`${API}/auth/me`)
      .then(res => setUser(res.data))
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setUser(null); });
  }, []);

  // Hydrate menu_visibility whenever we have an authenticated user
  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/settings`)
      .then(r => setMenuVisibility(r.data?.menu_visibility || DEFAULT_MENU_VISIBILITY))
      .catch(() => {});
  }, [user]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    // Cookie is set by server (httpOnly). Keep token in localStorage as Bearer fallback.
    localStorage.setItem(TOKEN_KEY, res.data.access_token);
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch {}
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    window.location.href = "/login";
  };

  // Utility: can the current user access a given menu key?
  //   1. Admin always bypasses
  //   2. Per-user menu_overrides (from /users) win if set
  //   3. Otherwise fall back to role → menu_visibility mapping
  const canAccess = (menuKey) => {
    if (!user) return false;
    if (user.role === "admin") return true;
    if (user.menu_overrides && Object.prototype.hasOwnProperty.call(user.menu_overrides, menuKey)) {
      return !!user.menu_overrides[menuKey];
    }
    const allowed = menuVisibility?.[menuKey] || DEFAULT_MENU_VISIBILITY[menuKey] || [];
    return allowed.includes(user.role);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, setUser, menuVisibility, setMenuVisibility, canAccess }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Role helpers
export const ROLES = {
  ADMIN: "admin",
  MANAGER: "manager",
  SALES_AGENT: "sales_agent",
  ACCOUNTS: "accounts"
};

export const ROLE_LABELS = {
  admin: "Admin",
  manager: "Manager",
  sales_agent: "Sales Agent",
  accounts: "Accounts",
  referrer: "Referrer"
};

export const hasRole = (user, ...roles) => user && roles.includes(user.role);
